import { Box, Button, Typography } from '@mui/material';

import { FilterAlt } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import { useFiltersCard } from './hooks';
import { Select } from 'ui';
import { t } from 'i18next';
import { theme } from '@store';
import { USStateSelect } from '@components';
import { MultiselectSearchableField } from 'components/Multiselect';

export const FiltersCard = () => {
  const {
    handleToggleFiltersCard,
    filters,
    resetFilters,
    handleApply,
    isResetDisabled,
    isApplyDisabled,
    handleChangePartnerFilter,
    handleChangeStateFilter,
    partnerOptions,
    certifiedOptions,
    practiceOptions,
    providerSpecialtyOptions,
    statusOptions,
    handleChangeCertifiedFilter,
    handleChangePracticeFilter,
    handleChangeProviderSpecialityFilter,
    handleChangeStatusFilter,
    regionOptions,
    clinicalLeaderOptions,
    supervisingPhysicianOptions,
    isRegionLoading,
    isClinicalLeaderLoading,
    isSupervisingPhysicianLoading,
    setRegionSearchQuery,
    setClinicalLeaderSearchQuery,
    setSupervisingPhysicianSearchQuery,
    handleChangeRegionFilter,
    handleChangeClinicalLeaderFilter,
    handleChangeSupervisingPhysicianFilter,
  } = useFiltersCard();

  return (
    <Box
      sx={{
        minWidth: '260px',
        maxWidth: '260px',
        boxShadow: 'initial',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          padding: '0',
          pt: '1rem',
          pb: '0.5rem',
          mb: '0.5rem',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <FilterAlt />
          <Typography variant='subtitle1'>Filters</Typography>
        </Box>
        <Box
          onClick={handleToggleFiltersCard}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            display: 'flex',
            ':hover': {
              cursor: 'pointer',
              bgcolor: theme => theme.palette.grey[50],
              borderRadius: '6px',
            },
            height: '30px',
            width: '30px',
          }}
        >
          <ClearIcon />
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          maxHeight: `calc(100vh - 465px)`,
          overflowY: 'auto',
          [theme.breakpoints.down('md')]: {
            maxHeight: 'calc(100vh - 310px)',
          },
          pt: 1,
        }}
      >
        <Select
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          name='guide_certified'
          label='GUIDE certified'
          value={filters.guide_certified}
          onChange={handleChangeCertifiedFilter}
          size='medium'
          options={certifiedOptions}
          clearLabel='All'
          clearable
        />

        <USStateSelect
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          name='state'
          value={filters.state}
          onChange={handleChangeStateFilter}
          clearLabel='All'
          clearable
        />

        <Select
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          name='partner'
          label='Partner'
          value={filters.partner_id}
          onChange={handleChangePartnerFilter}
          size='medium'
          options={partnerOptions}
          clearLabel='All'
          clearable
        />

        <Select
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          name='practice'
          label='Practice'
          value={filters.practice_id}
          onChange={handleChangePracticeFilter}
          size='medium'
          options={practiceOptions}
          clearLabel='All'
          clearable
        />

        <Select
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          name='provider_specialty'
          label='Specialty'
          value={filters.provider_specialty}
          onChange={handleChangeProviderSpecialityFilter}
          size='medium'
          options={providerSpecialtyOptions}
          clearLabel='All'
          clearable
        />

        <Select
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          name='status'
          label='Status'
          value={filters.status}
          onChange={handleChangeStatusFilter}
          size='medium'
          options={statusOptions}
          clearLabel='All'
          clearable
        />

        <MultiselectSearchableField
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          options={regionOptions}
          value={filters.region_ids}
          name='region_ids'
          label='Region'
          handleChange={handleChangeRegionFilter}
          onInputChange={setRegionSearchQuery}
          loading={isRegionLoading}
          optional
        />

        <MultiselectSearchableField
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          options={clinicalLeaderOptions}
          value={filters.clinical_leader_ids}
          name='clinical_leader_ids'
          label='Clinical leader'
          handleChange={handleChangeClinicalLeaderFilter}
          onInputChange={setClinicalLeaderSearchQuery}
          loading={isClinicalLeaderLoading}
          optional
        />

        <MultiselectSearchableField
          sx={{
            backgroundColor: theme => theme.palette.common.white,
          }}
          options={supervisingPhysicianOptions}
          value={filters.supervising_physician_ids}
          name='supervising_physician_ids'
          label='Supervising physician'
          handleChange={handleChangeSupervisingPhysicianFilter}
          onInputChange={setSupervisingPhysicianSearchQuery}
          loading={isSupervisingPhysicianLoading}
          optional
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: '0.5rem',
          width: '100%',
          py: '1rem',
        }}
      >
        <Button
          variant='outlined'
          onClick={resetFilters}
          sx={{ width: '100%', maxHeight: '2rem' }}
          disabled={isResetDisabled}
        >
          {t('general.reset')}
        </Button>
        <Button
          onClick={handleApply}
          variant='contained'
          color='secondary'
          loadingPosition='center'
          sx={{
            color: 'white',
            width: '100%',
            maxHeight: '2rem',
          }}
          disabled={isApplyDisabled}
        >
          {t('general.apply')}
        </Button>
      </Box>
    </Box>
  );
};
